import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Field, getFormValues, getFormMeta, change } from "redux-form";
import { ReduxControls } from "rhombus-react";

import { SOCIAL_WIZARD } from "widgets/forms/list";

import { buildFacebookPages } from "selectors/Social";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { FACEBOOK_IMAGE_CONSTRAINTS } from "social/validators/facebook";
import { facebookPagesTextProps, instagramTextProps } from "social/constants/common";
import {
	mapStringToOption,
	normalizeOptionToString,
	formatAPIOption,
	normalizeAPIOption
} from "social/utils";
import { noop } from "utils/common";
import { FB_CTA_OPTIONS } from "social/constants/CTA";

import "./FacebookMainContent.css";

const { MAX_LENGTH_LANDING_PAGE } = FACEBOOK_IMAGE_CONSTRAINTS;

const FACEBOOK_CAROUSEL_FIELDS = {
	creativeName: {
		name: "name",
		label: "Creative Name",
		className: "full-width cypress-creative-name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	facebookPage: {
		name: "facebookPage",
		label: "Facebook Page",
		className: "half-width cypress-facebook-page",
		component: ReduxControls.Select,
		required: true,
		normalize: normalizeAPIOption,
		format: formatAPIOption,
		placeholder: "Select...",
	},
	instagramAccount: {
		name: "instagramAccount",
		label: "Instagram Account",
		className: "half-width cypress-instagram-account",
		component: ReduxControls.Select,
		normalize: normalizeAPIOption,
		format: formatAPIOption,
		placeholder: "Select...",
	},
	primaryText: {
		name: "primaryText",
		label: "Primary Text",
		className: "full-width cypress-primary-text",
		placeholder: "Tell people what your creative is about",
		tip: {
			description: "May get truncated after 125 characters",
			direction: "right",
		},
		component: ReduxControls.Input,
		type: "textarea",
	},
	cta: {
		name: "cta",
		label: "CTA",
		className: "half-width cypress-cta-select",
		placeholder: "Enter Call to Action Text",
		component: ReduxControls.Select,
		options: FB_CTA_OPTIONS,
		normalize: normalizeOptionToString,
		format: mapStringToOption,
		isClearable: false,
		required: true,
		onMenuScrollToBottom: noop,
	},
	seeMoreCard: {
		name: "seeMoreCard",
		label: "Display a \"See More\" card at the end",
		className: "half-width cypress-see-more",
		component: ReduxControls.Checkbox,
		type: "checkbox",
	},
	seeMoreDisplayLink: {
		name: "seeMoreDisplayLink",
		label: "See More Display Link",
		className: "full-width cypress-see-more-display-link",
		placeholder: "Enter link text to be displayed",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
	},
	seeMoreDisplayUrl: {
		name: "seeMoreDisplayUrl",
		label: "See More URL",
		className: "full-width cypress-see-more-url",
		placeholder: "https://www.example.com",
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
		required: true,
	},
};

function fieldGenerator(props) {
	const {
		facebookPages=[],
		instagramAccounts=[],
		seeMoreCard,
		showPristineErrors,
		isViewOnly,
	} = props;

	const seeMoreCardFields = ["seeMoreDisplayLink", "seeMoreDisplayUrl"];

	return (name) => {
		const fieldProps = FACEBOOK_CAROUSEL_FIELDS[name];

		let extraProps = {
			showPristineErrors,
			disabled: isViewOnly,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		if(!seeMoreCard && seeMoreCardFields.includes(name)){
			return;
		}

		if(fieldProps.name === "facebookPage"){
			if(!facebookPages.length) {
				extraProps = {
					...extraProps,
					...facebookPagesTextProps,
				};
			} else {
				extraProps.options = facebookPages;
				// We only want to add `onMenuScrollToBottom` for the Select input
				extraProps.onMenuScrollToBottom = noop;
			}
		}

		if(fieldProps.name === "instagramAccount"){
			if(!instagramAccounts.length){
				extraProps = {
					...extraProps,
					...instagramTextProps,
				};
			} else {
				extraProps.options = instagramAccounts;
				// We only want to add `onMenuScrollToBottom` for the Select input
				extraProps.onMenuScrollToBottom = noop;
			}
		}

		return (
			<Field
				key={fieldProps.name}
				{...fieldProps}
				{...extraProps}
			/>
		);
	};
}

export default function FacebookMainContent(props) {
	const {
		adType,
		isEditMode,
		isViewOnly,
		showPristineErrors = false,
	} = props;

	const dispatch = useDispatch();

	const { formValues, meta } = useSelector((state) => {
		const formValues = getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {};
		const meta = getFormMeta(SOCIAL_WIZARD)(state)?.[adType] || {};

		return {
			formValues,
			meta,
		};
	});
	const { seeMoreCard, instagramAccount, facebookPage } = formValues;
	const { facebookPages, instagramAccounts } = useSelector(buildFacebookPages);

	useEffect(() => {
		if(isEditMode){ return; }

		const isTouchedFacebookPage = meta.facebookPage?.touched;

		if(!facebookPage && !isTouchedFacebookPage) {
			dispatch(change(SOCIAL_WIZARD, `${adType}.facebookPage`, facebookPages?.[0]));
		}

		const isTouchedInstagramAccount = meta.instagramAccount?.touched;

		if(!instagramAccount && !isTouchedInstagramAccount) {
			dispatch(change(SOCIAL_WIZARD, `${adType}.instagramAccount`, instagramAccounts?.[0]));
		}
	}, [facebookPages, instagramAccounts]);

	const generateFields = fieldGenerator({
		seeMoreCard,
		facebookPages,
		instagramAccounts,
		isViewOnly,
		showPristineErrors,
	});
	const fields = useMemo(() => (
		Object.keys(FACEBOOK_CAROUSEL_FIELDS).map(generateFields)
	), [adType, facebookPages, instagramAccounts, seeMoreCard]);

	return (
		<div className="form-container">
			<div className="fields">
				{ fields }
			</div>
		</div>
	);
}
